const VALID_TAG_NAME = /^[a-zA-Z][a-zA-Z0-9.:_-]*$/;

// The HTML tokenizer ends a tag name at whitespace, '/' or '>'. Note '"' is NOT
// a terminator, which is exactly why dealer text like `<data-href="` yields a
// tag named `data-href="` instead of being treated as an attribute.
const isTagNameEnd = (char) => char === '/' || char === '>' || /\s/.test(char);

const isLetter = (char) => /[a-zA-Z]/.test(char || '');

// '<' opens a tag only when followed by a letter (or '/' + letter). Anything
// else — `Price < $30,000`, `<3`, `<!-- comment -->` — is text or a bogus
// comment to the parser and must be left alone.
const isTagStart = (str, index) =>
	str[index + 1] === '/'
		? isLetter(str[index + 2])
		: isLetter(str[index + 1]);

const readTagName = (str, from) => {
	let end = from;
	while (end < str.length && !isTagNameEnd(str[end])) {
		end += 1;
	}
	return { name: str.slice(from, end), end };
};

// Index of the '>' that closes this tag. Quotes only open an attribute value
// when they follow '=' — a quote anywhere else is part of an attribute *name*
// (`<data-href=" /x">` closes at its '>'), which is how the parser reads it.
const findTagEnd = (str, from) => {
	let quote = null;
	let prevSignificant = '';

	for (let i = from; i < str.length; i += 1) {
		const char = str[i];

		if (quote) {
			if (char === quote) {
				quote = null;
			}
		} else if ((char === '"' || char === "'") && prevSignificant === '=') {
			quote = char;
		} else if (char === '>') {
			return i;
		}

		if (!/\s/.test(char)) {
			prevSignificant = char;
		}
	}

	// Unterminated tag at end of input — the parser drops it, so do the same.
	return str.length - 1;
};

/**
 * Removes tag tokens whose tag name the DOM would reject, keeping the text
 * around them. Valid markup is returned byte-for-byte unchanged.
 *
 * Targeted scanner, not an HTML parser: it locates tag-name tokens so it can
 * decide keep-or-drop, and steps over each tag as a unit so quoted attribute
 * values are never re-scanned. Remaining imprecision is confined to the inside
 * of an already-invalid tag, where the parser's own reading is equally odd.
 *
 * @param {*} value label text (any other type is returned untouched)
 * @returns {*} the same value with invalid tag tokens removed
 */
export const sanitizeLabelMarkup = (value) => {
	if (typeof value !== 'string' || !value.includes('<')) {
		return value;
	}

	let out = '';
	let i = 0;

	while (i < value.length) {
		if (value[i] !== '<' || !isTagStart(value, i)) {
			out += value[i];
			i += 1;
		} else {
			const nameFrom = value[i + 1] === '/' ? i + 2 : i + 1;
			const { name, end } = readTagName(value, nameFrom);

			const tagEnd = findTagEnd(value, end);

			if (VALID_TAG_NAME.test(name)) {
				out += value.slice(i, tagEnd + 1);
			}

			i = tagEnd + 1;
		}
	}

	return out;
};

/**
 * Applies sanitizeLabelMarkup to every own string value of an object.
 *
 * @param {object} source map of label key -> label text
 * @returns {object} the same shape with sanitized values
 */
export const sanitizeLabelMap = (source) => {
	if (!source || typeof source !== 'object') {
		return source;
	}

	return Object.keys(source).reduce((acc, key) => {
		acc[key] = sanitizeLabelMarkup(source[key]);
		return acc;
	}, {});
};

/**
 * Sanitizes the free-text titles on an incentive map. getPriceItemLabel() in
 * wsm-pricing-display can return incentives[id].title / .shortTitle instead of
 * a label key, so those strings reach the parser too.
 *
 * @param {object} incentives map of incentive id -> incentive
 * @returns {object} the same shape with sanitized titles
 */
export const sanitizeIncentiveTitles = (incentives) => {
	if (!incentives || typeof incentives !== 'object') {
		return incentives;
	}

	return Object.keys(incentives).reduce((acc, key) => {
		const incentive = incentives[key];
		acc[key] =
			incentive && typeof incentive === 'object'
				? {
						...incentive,
						title: sanitizeLabelMarkup(incentive.title),
						shortTitle: sanitizeLabelMarkup(incentive.shortTitle)
				  }
				: incentive;
		return acc;
	}, {});
};

export default sanitizeLabelMarkup;
